import { Refresh, Save } from '@mui/icons-material';
import { useNotify } from 'react-admin';
import { useEffect, useState } from 'react';
import { goApi } from '../../services';
import { apiRoutes } from '../../services/apiRoutes';
interface BatchTemplateSubject {
    id: string;
    subjectId: string;
    name: string;
}

const toISODateTime = (time: string) => {
    const today = new Date().toISOString().split('T')[0];
    return `${today}T${time}:00Z`;
};

const parseTimeFromAPI = (time: string | null) => {
    if (!time) return '';
    // Handle formats like "00:40:00+00" or ISO datetime
    const match = time.match(/(\d{2}):(\d{2})/);
    return match ? `${match[1]}:${match[2]}` : '';
};

interface BatchScheduleTemplate {
    id: string;
    batchTemplateSubjectId: string;
    day: string;
    startTime: string | null;
    endTime: string | null;
}

const BatchScheduleTemplates = ({ batch }: { batch: any }) => {
    const [loading, setLoading] = useState(false);
    const [subjects, setSubjects] = useState<any[]>([]);
    const templateId = batch?.templateId;
    useEffect(() => {
        const fetchSubjects = async () => {
            if (!templateId) return;
            setLoading(true);
            try {
                const response = await goApi.get(
                    `${apiRoutes.BATCH_TEMPLATE_SUBJECTS}?batchTemplateId=${templateId}&limit=50`,
                );
                setSubjects(response.data?.items || []);
            } catch (err) {
                console.error('Failed to fetch subjects', { type: 'error' });
            } finally {
                setLoading(false);
            }
        };
        fetchSubjects();
    }, [templateId]);

    if (!subjects?.length && !loading) {
        return <div>No Subject Added to batch Template</div>;
    }
    return <BatchScheduleTemplatesList batchTemplateSubjects={subjects} batch={batch} />;
};

const BatchScheduleTemplateComponent = ({
    day,
    subject,
    existingValue,
    batch,
}: {
    existingValue?: BatchScheduleTemplate;
    subject: any;
    day: string;
    batch: any;
}) => {
    const notify = useNotify();
    const [id, setId] = useState<string | undefined>(existingValue?.id);
    const [startTime, setStartTime] = useState<string | any>(null);
    const [endTime, setEndTime] = useState<string | any>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setStartTime(parseTimeFromAPI(existingValue?.startTime || null));
        setEndTime(parseTimeFromAPI(existingValue?.endTime || null));
        setId(existingValue?.id);
    }, [existingValue]);

    const handleSave = async () => {
        if (loading || !startTime || !endTime) return;
        setLoading(true);
        try {
            if (!id) {
                const res = await goApi.post<{ data: { id: string } }>(
                    `/batches/batch-schedule-templates`,
                    {
                        batchTemplateSubjectId: subject?.id,
                        batchId: batch.id,
                        day,
                        startTime: toISODateTime(startTime),
                        endTime: toISODateTime(endTime),
                    },
                );
                setId(res.data.id);
                notify('Schedule template created', { type: 'success' });
            } else {
                await goApi.put(`/batches/batch-schedule-templates/${id}`, {
                    startTime: toISODateTime(startTime),
                    endTime: toISODateTime(endTime),
                });
                notify('Schedule template updated', { type: 'success' });
            }
        } catch (err: any) {
            notify(err.message || 'Unable to save schedule template', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };
    const handleReset = async () => {
        if (!id || loading) return;

        setLoading(true);
        try {
            await goApi.delete(`/batches/batch-schedule-templates/${id}`);
            setId(undefined);
            setStartTime('');
            setEndTime('');
            notify('Schedule template reset', { type: 'success' });
        } catch (err: any) {
            notify(err.message || 'Unable to reset schedule template', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className={'flex-1'}>{day}</div>

            <div className={'border rounded-md overflow-hidden py-0.5 px-2'}>
                <input
                    value={startTime}
                    onChange={e => setStartTime(e.target.value)}
                    placeholder={'Start Time'}
                    type={'time'}
                />
            </div>

            <div className={'border rounded-md overflow-hidden py-0.5 px-2'}>
                <input
                    value={endTime}
                    onChange={e => setEndTime(e.target.value)}
                    placeholder={'End Time'}
                    type={'time'}
                />
            </div>

            <div>
                <Save onClick={handleSave} color={'success'} className={'cursor-pointer'} />
            </div>
            <div>
                <Refresh onClick={handleReset} color={'info'} className={'cursor-pointer'} />
            </div>
        </>
    );
};

const BatchScheduleTemplatesList = ({
    batchTemplateSubjects,
    batch,
}: {
    batchTemplateSubjects: BatchTemplateSubject[];
    batch: any;
}) => {
    const notify = useNotify();
    const [scheduleTemplates, setScheduleTemplates] = useState<BatchScheduleTemplate[]>([]);
    const daysOfTheWeek = [
        'MONDAY',
        'TUESDAY',
        'WEDNESDAY',
        'THURSDAY',
        'FRIDAY',
        'SATURDAY',
        'SUNDAY',
    ];

    useEffect(() => {
        if (!batchTemplateSubjects.length || !batch?.id) return;

        const ids = batchTemplateSubjects.map(b => b.id).join(',');
        goApi
            .get<{ data: { items: BatchScheduleTemplate[] } }>(
                `/batches/batch-schedule-templates?batchTemplateSubjectIds=${ids}&page=1&limit=100`,
            )
            .then(res => setScheduleTemplates(res.data.items))
            .catch(err =>
                notify(err.message || 'Failed to fetch schedule templates', { type: 'error' }),
            );
    }, [batchTemplateSubjects, batch?.id]);

    return (
        <div className={'p-2 space-y-2'}>
            <div className={'grid grid-cols-1 lg:grid-cols-2 gap-4'}>
                {batchTemplateSubjects.map((subject, index) => {
                    const batchTemplateSubjectId = subject.id;
                    return (
                        <div key={index} className={'space-y-2'}>
                            <div className={'py-1 font-extrabold'}>{subject.name}</div>
                            <div
                                className={
                                    'flex p-2 rounded-md border space-x-2 items-center bg-gray-100'
                                }
                            >
                                <div className={'flex-1'}>Day of the Week</div>
                                <div className={'border rounded-md py-0.5 px-2 w-[120px]'}>
                                    Start Time
                                </div>
                                <div className={'border rounded-md py-0.5 px-2 w-[120px]'}>
                                    End Time
                                </div>
                                <div className={'w-[24px]'} />
                                <div className={'w-[24px]'} />
                            </div>
                            {daysOfTheWeek.map((day, index) => (
                                <div
                                    key={index}
                                    className={'flex p-2 items-center rounded-md border space-x-2'}
                                >
                                    <BatchScheduleTemplateComponent
                                        day={day}
                                        subject={subject}
                                        existingValue={scheduleTemplates.find(
                                            a =>
                                                a.batchTemplateSubjectId ===
                                                    batchTemplateSubjectId && a.day === day,
                                        )}
                                        batch={batch}
                                    />
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default BatchScheduleTemplates;
